/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { RootState } from "../../../../reducers";
import { LAYER_TYPE } from "../../../../actions/Layers/constants";
import { addBrowseHistory } from "../../../../actions/Navigation/actions";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../../actions/Tenants/config/constants";
import {
    usePatchLayerMutation
} from "../../../../hooks/mutations";
import { useCurrentLayer, useOfflineMode, useRouteLayer, useRouteLayerQuery } from "../../../../hooks/useCurrentEntities";
import { useConfiguredLayerChecks, useLayerChecksInvalidation } from "../../../../hooks/useLayerChecks";
import { onReplaySuccess } from "../../../../offline/replayEngine";
import { isFilledArray } from "../../../../utils";
import { useRegisterDebugTargets } from "../../../../utils/useDebugTargets";
import { Loading } from "../../../Helper/Loading";
import SidebarPageLayout from "../../Layout/SidebarPageLayout";
import ArrivalCheck from "./ArrivalCheck";
import BoxWeightCheck from "./BoxWeightCheck";
import LabCheck from "./LabCheck";
import LayerBreadcrumbs from "./LayerBreadcrumbs";
import LayerChecks from "./LayerChecks";
import LayerLocationHistory from "./LayerLocationHistory";
import { LayerMeta } from "./LayerMeta";
import LayerMetricCardsGroup from "./LayerMetricCardsGroup";
import LayerNotes from "./LayerNotes";
import LayerParentsChildren from "./LayerParentsChildren";
import LayerReportsView from "./LayerReportsView";
import RipeningGraph from "./RipeningGraph";
import { SpecialActionComponents } from "./SpecialActions";
import StartCheckForm from "./StartCheckForm";
import SubLayers from "./SubLayers";
import SubLayerTabs from "./SubLayerTabs";

const contentWrapper = css`
    gap: 1.5rem;

    > * {
        border-top: 1px solid #f4f9ff;
        padding-top: 3rem;
    }

    > *:empty {
        display: none;
    }
`;

export default function LayerIndex() {
    const params = useParams();
    const layer = useRouteLayer();
    const isLoading = useRouteLayerQuery().isLoading;
    const user = useSelector((state: RootState) => state.auth.user);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const location = useLocation();
    const config = useConfig();
    const inOfflineMode = useOfflineMode();

    // Mutation hooks
    const patchLayerMutation = usePatchLayerMutation();
    const tenant_id = (user as any)?.tenant?.tenant_id;
    const routeTenantId = params.tenant_id as string;

    // * Register layer_type shortcut (direct link to config)
    const layer_type_config = layer?.type ? config.configs.find((c) => c.value === layer.type && c.type === CONFIG_TYPES.LAYER_TYPE) : null;
    useRegisterDebugTargets("layer_type", tenant_id && layer_type_config ? [{
        id: `layer_type_${layer_type_config.id}`,
        url: `/tenants/${tenant_id}/config/${layer_type_config.id}`,
        text: `${layer_type_config.config.text} config`,
    }] : []);

    // * Register layer data inspection modal
    useRegisterDebugTargets("layer_data", tenant_id && layer ? [{
        id: `layer_data_${layer.id}`,
        url: "#",
        text: "Layer data",
        debug: {
            props: { data: layer },
            title: `Debug ${layer_type_config?.config.text || "Layer"} ${layer.label}`,
        },
    }] : []);
    const layer_config = config.get_layer_config(layer);
    const checkLocations = config.get_check_types_config(layer);
    const currentLayerQuery = useCurrentLayer(routeTenantId, params.layer_id as string);

    // * Get underlying checks if we need them
    // * Checks are used in calender view
    // * Checks are used to get set latest check in LayerMeta (bad practice, use copy_check_meta instead)
    // * Checks are used in Sublayers (Children table) to set latest check (bad practice, use copy_check_meta instead)

    // * By default load the checks done on the layer itself
    // * Fetch checks for the layer using TanStack Query
    // * This initializes the cache; child components call the same hook and get cached data
    const { data: checks = [], view_strategy } = useConfiguredLayerChecks(routeTenantId, layer, layer_config);
    // Force the checks query to refetch after an offline check syncs (see effect below).
    const { invalidateAllLayerChecks } = useLayerChecksInvalidation(routeTenantId, layer?.id);

    // Offline-check sync: when an offline session that targeted *this*
    // layer finishes replaying to the server, re-hydrate the page so
    // the just-synced check appears in the layer's check list without
    // requiring the controller to navigate away and back. Matches on
    // the cached_layer.id captured at scan time — the same value that
    // populates the /layer/:layer_id route param when arriving here
    // via the offline flow. Sessions for other layers are ignored.
    // The subscription unsubscribes / re-binds when params.layer_id or
    // view_strategy change so the closure always sees current values.
    useEffect(() => {
        const layerId = params.layer_id;
        if (!layerId) return undefined;
        return onReplaySuccess(({ cachedLayerId }) => {
            if (cachedLayerId !== layerId) return;
            // Keep the existing layer on screen until the response lands so
            // the page doesn't flash to <Loading /> during the refresh.
            currentLayerQuery.refetch();
            queryClient.invalidateQueries({ queryKey: ["layerLocationHistory", routeTenantId, layerId] });
            // Invalidate the checks query so the just-synced check appears:
            // layer.id won't change across a same-id refresh, so the
            // useLayerChecks query above won't refetch on its own. We
            // invalidate the whole "layerChecks" key (rather than the
            // layer-scoped one) because this callback's closure can't see
            // the current layer.id reliably, and it's already guarded to
            // fire only for this layer.
            invalidateAllLayerChecks();
        });
    }, [params.layer_id, view_strategy, routeTenantId]);

    useEffect(() => {
        if (layer?.id !== params.layer_id || !layer_config?.text || !location?.pathname) {
            return;
        }

        const layerType = config.get_layer_config(layer);

        dispatch(
            addBrowseHistory({
                pathname: location.pathname,
                id: layer.id,
                label: layer.label,
                text: layer_config.text,
                level: layerType?.position,
            })
        );
    }, [layer?.id, layer_config?.text, location?.pathname]);

    useEffect(() => {
        if (!layer?.id || !layer_config?.onload_callback) {
            return;
        }
        const _patchLayer = (field: string, value: any, callApi = false) => {
            patchLayerMutation.mutate({ layer, field, value, keep_history: callApi });
        };
        const callbackProps = { layer, patchLayer: _patchLayer, checks, config };
        if (typeof layer_config.onload_callback === "function") {
            layer_config.onload_callback(callbackProps);
        } else if (typeof layer_config.onload_callback === "string") {
            const callback = config.onload_layer_callbacks?.[layer_config.onload_callback];
            if (typeof callback === "function") {
                callback(callbackProps);
            } else {
                console.error(`onload_layer_callback "${layer_config.onload_callback}" not found`); // eslint-disable-line no-console
            }
        }
    }, [JSON.stringify(layer), JSON.stringify(checks), routeTenantId]);

    // Offline, offer only what an offline session can carry, as the scanner
    // does (CreateLayer): check types explicitly marked offline_enabled
    // (strictly `true`; absent means not opted in), and no special actions,
    // which create layers and so need the server.
    const specialActions = inOfflineMode ? [] : (layer_config?.special_actions || []);
    const startableCheckLocations = (checkLocations || []).filter((i) => !i.is_hidden
        && (!inOfflineMode || (i as { offline_enabled?: unknown }).offline_enabled === true));

    const startCheckButtons = [
        ...specialActions.map((i, index) => ({
            position: i.position || (index + 1) * 10,
            el: (
                <div key={index} className="d-inline-flex d-lg-block">
                    {SpecialActionComponents[i.type](i)}
                </div>
            ),
        })),
        ...startableCheckLocations.map((i, index) => ({
            position: i.position,
            el: <div key={index + 0.5} className="d-inline-flex d-lg-block">
                <StartCheckForm location_config={i} />
            </div>
        }))
    ].sort((a, b) => (a.position || 0) - (b.position || 0)).map((i) => i.el);

    const content = useMemo(() => {
        if (layer_config) {
            const elements = [
                isFilledArray(layer_config.show_metric_cards) && <LayerMetricCardsGroup />,
                layer_config.show_checks && <LayerChecks subconfig={layer_config.show_checks} />,
                layer_config.show_box_weight_check && <BoxWeightCheck title={layer_config.show_box_weight_check?.title || ""} />,
                layer_config.show_arrival_check && <ArrivalCheck subconfig={layer_config.show_arrival_check?.fields} title={layer_config.show_arrival_check?.title} />,
                layer_config?.show_advance_ripening && <SubLayers subconfig={layer_config?.show_advance_ripening} show_art={true} children_layer_type={layer_config?.children_layer_type} />,
                layer_config?.show_ripening_progress && <RipeningGraph />,
                layer_config?.show_children && <SubLayers subconfig={layer_config?.show_children} children_layer_type={layer_config?.children_layer_type} />,
                isFilledArray(layer_config?.show_tabs) && <SubLayerTabs tabs={layer_config.show_tabs ?? []}children_layer_type={layer_config?.children_layer_type} />,
                layer_config.show_lab_check && <LabCheck title={layer_config.show_lab_check?.title || ""} />,
                layer_config?.show_general_notes && <LayerNotes />,
            ].filter((el) => !!el);

            return elements.map((el, index) => (
                <div className="w-100" key={index}>
                    {el}
                </div>
            ));
        }

        return null;
    }, [layer_config]);

    // Do not unmount the whole page when calling getLayer after an update
    if (isLoading && String(layer.id) !== params.layer_id) {
        return <Loading />;
    }

    if (!layer_config) {
        return null;
    }

    // TODO: put start check button in bottomBar
    return (
        <SidebarPageLayout
            title={`${layer_config?.text} ${layer.label}`}
            breadcrumbs={<LayerBreadcrumbs />}
            sidebar={
                <>
                    <LayerMeta />
                    {layer_config?.show_upload_reports_button && LAYER_TYPE.PURCHASE_ORDER && <LayerReportsView layer={layer} />}
                    <LayerParentsChildren />
                    <LayerLocationHistory />
                </>
            }
        >
            <div className="pb-4 d-flex flex-wrap align-items-center gap-1">
                {startCheckButtons}
            </div>

            <div className="w-100 d-flex flex-column" css={contentWrapper}>
                {content}
            </div>
        </SidebarPageLayout>
    );
}
